import type { GatewayBrowserClient } from "../gateway.ts";

export type PalaceContinuityPoint = {
  id: string;
  sessionId: string;
  timestamp: string;
  category: string;
  preview: string;
  body: string;
  path: string;
  startLine: number;
  endLine: number;
};

export type StructuredTaskState = {
  taskId: string;
  description: string;
  category: string;
  status: string;
  subTasks: { subTaskId: string; description: string; status: string }[];
  workerAgentId?: string;
  lastUpdateAt: number;
  findings?: string;
  constraint?: string;
};

export type SessionContinuityPayload = {
  activeTask?: StructuredTaskState;
  pendingSubTasks: StructuredTaskState["subTasks"];
  recentDecisions: { id: string; content: string; timestamp: number }[];
  constraints: { text: string; source: string }[];
  lastWorkerFindings?: string;
};

export type PalaceMemorySnippet = {
  path: string;
  snippet: string;
  score?: number;
};

export type PalaceContinuitySnapshot = {
  sessionKey: string;
  sessionId: string;
  points: PalaceContinuityPoint[];
  totalChunks: number;
  backend: string | null;
  structuredContinuity?: SessionContinuityPayload;
  memorySnippets?: PalaceMemorySnippet[];
  /** True when Redis/MemPrism soft-timed out; disk drops may still be present. */
  timedOut?: boolean;
};

export type ChatPalaceContinuityState = {
  client: GatewayBrowserClient | null;
  connected: boolean;
  sessionKey: string;
  chatPalaceContinuityOpen: boolean;
  chatPalaceContinuityLoading: boolean;
  chatPalaceContinuityClearing: boolean;
  chatPalaceContinuityMutating: boolean;
  chatPalaceContinuityError: string | null;
  chatPalaceContinuity: PalaceContinuitySnapshot | null;
  chatPalaceContinuityFlash?: boolean;
  chatPalaceContinuityDraft: string;
  chatPalaceContinuityDraftCategory: "fact" | "decision";
  /**
   * JAISIU-3327 — last background Palace continuity fetch (ms). Shared
   * between the polling loop and the chat.final-triggered refresh so
   * both paths honor the same throttle.
   */
  chatPalaceContinuityLastPollAt?: number;
  /**
   * JAISIU-XXXX — per-session cache of Palace continuity snapshots. The
   * active `chatPalaceContinuity` only holds the snapshot for the
   * operator's currently-selected session; this cache holds snapshots
   * for OTHER sessions that received updates via `chat.final` while we
   * were viewing elsewhere. When the operator switches back to a cached
   * session, the inspector dropdown shows the latest data immediately
   * (no flash of "no continuity" between switch and the next chat.final).
   *
   * Bounded growth: capped at 32 entries; oldest (by last updated ts)
   * is evicted on overflow. 32 covers the common case of one operator
   * flipping between a handful of sessions without exhausting memory.
   */
  chatPalaceContinuityCache?: Map<string, PalaceContinuitySnapshot>;
  chatPalaceContinuityCacheLimit?: number;
  /**
   * In-flight `sessions.palaceContinuity.add` request markers (one per
   * active RPC). The UI uses these to:
   *   1. Block duplicate-add clicks for the same draft via the
   *      `chatPalaceContinuityMutating` guard in `addChatPalaceContinuityPoint`.
   *   2. Surface a "Saving…" badge in the dropdown while the RPC is in
   *      flight, so the operator gets feedback during slow gateway
   *      responses instead of staring at a frozen UI.
   *   3. Reconcile on reconnect — if a response never arrives, the
   *      pending marker is cleared in `finally` and the next snapshot
   *      fetch reflects whatever actually persisted server-side.
   *
   * The `sessions.palaceContinuity.add` schema is strict
   * (`additionalProperties: false`) and does NOT accept a wire-level
   * idempotency key, so this Set carries opaque local markers, not
   * server-dedupe keys. Server-side dedupe is a separate ticket.
   */
  chatPalaceContinuityPending?: Set<string>;
};

/**
 * JAISIU-3327 — minimum gap between background Palace continuity fetches.
 * The dropdown-open path bypasses this so the operator always sees the
 * latest points while editing.
 */
export const PALACE_CONTINUITY_POLL_MIN_GAP_MS = 8_000;

let palaceFlashTimer: ReturnType<typeof setTimeout> | undefined;

export function triggerPalaceContinuityFlash(state: ChatPalaceContinuityState) {
  state.chatPalaceContinuityFlash = true;
  if (palaceFlashTimer) {
    clearTimeout(palaceFlashTimer);
  }
  palaceFlashTimer = setTimeout(() => {
    state.chatPalaceContinuityFlash = false;
    palaceFlashTimer = undefined;
  }, 900);
}

export function resetChatPalaceContinuityForSessionChange(state: ChatPalaceContinuityState) {
  state.chatPalaceContinuityOpen = false;
  // JAISIU-XXXX — preserve `chatPalaceContinuity` from the cache if a
  // snapshot was stashed for this session key via a background
  // `refreshChatPalaceContinuityAfterRun`. The previous behavior wiped
  // the snapshot wholesale, so switching back to a session that
  // received a chat.final while we were elsewhere would show stale
  // "no continuity" until the next run. The cache survives the
  // wipe and is the new source of truth for non-current sessions.
  const trimmedKey = state.sessionKey.trim();
  if (trimmedKey && state.chatPalaceContinuityCache?.has(trimmedKey)) {
    state.chatPalaceContinuity = state.chatPalaceContinuityCache.get(trimmedKey) ?? null;
  } else {
    state.chatPalaceContinuity = null;
  }
  state.chatPalaceContinuityError = null;
  state.chatPalaceContinuityLoading = false;
}

/**
 * JAISIU-XXXX — stash a snapshot into the per-session cache. Caller is
 * responsible for picking the right key (typically `payload.sessionKey`
 * from a `chat.final` event), and for bumping the cache's oldest-key
 * eviction logic when the size limit is reached.
 */
export function cacheChatPalaceContinuity(
  state: ChatPalaceContinuityState,
  sessionKey: string,
  snapshot: PalaceContinuitySnapshot,
): void {
  const key = sessionKey.trim();
  if (!key) return;
  if (!state.chatPalaceContinuityCache) {
    state.chatPalaceContinuityCache = new Map();
  }
  state.chatPalaceContinuityCache.set(key, snapshot);
  // Bounded growth: evict oldest (insertion order) beyond the limit.
  const limit = state.chatPalaceContinuityCacheLimit ?? 32;
  while (state.chatPalaceContinuityCache.size > limit) {
    const oldest = state.chatPalaceContinuityCache.keys().next().value;
    if (oldest == null) break;
    state.chatPalaceContinuityCache.delete(oldest);
  }
}

export function snapshotMatchesSession(
  snapshot: PalaceContinuitySnapshot | null | undefined,
  sessionKey: string,
): boolean {
  const key = sessionKey.trim();
  if (!key || !snapshot) {
    return false;
  }
  return snapshot.sessionKey.trim() === key;
}

export async function refreshChatPalaceContinuityAfterRun(state: ChatPalaceContinuityState) {
  const prevTotal = state.chatPalaceContinuity?.totalChunks ?? 0;
  const prevLatest = state.chatPalaceContinuity?.points?.[0]?.timestamp ?? "";
  // JAISIU-3327 — throttle background chat.final refreshes so a rapid
  // sequence of assistant replies (e.g. a live cache smoke test with many
  // turns) does not hammer the gateway with sessions.palaceContinuity RPCs.
  // The dropdown-open path always fetches so the operator sees fresh
  // snippets while editing.
  const now = Date.now();
  const lastPoll = state.chatPalaceContinuityLastPollAt ?? 0;
  const dropdownOpen = state.chatPalaceContinuityOpen === true;
  if (!dropdownOpen && lastPoll > 0 && now - lastPoll < PALACE_CONTINUITY_POLL_MIN_GAP_MS) {
    return;
  }
  const attempted = await loadChatPalaceContinuity(state, {
    quiet: true,
    includeMemorySnippets: dropdownOpen,
  });
  if (attempted) {
    state.chatPalaceContinuityLastPollAt = Date.now();
  }
  const nextTotal = state.chatPalaceContinuity?.totalChunks ?? 0;
  const nextLatest = state.chatPalaceContinuity?.points?.[0]?.timestamp ?? "";
  if (nextTotal > prevTotal || (nextLatest && nextLatest !== prevLatest)) {
    triggerPalaceContinuityFlash(state);
  }
}

function isUnknownPalaceContinuityMethod(err: unknown): boolean {
  return String(err).includes("unknown method: sessions.palaceContinuity");
}

export function formatPalaceContinuityError(err: unknown): string {
  if (isUnknownPalaceContinuityMethod(err)) {
    return "Palace continuity needs a newer gateway. Run pnpm build, restart the gateway, and refresh.";
  }
  return String(err);
}

export async function loadChatPalaceContinuity(
  state: ChatPalaceContinuityState,
  opts?: { quiet?: boolean; includeMemorySnippets?: boolean },
): Promise<boolean> {
  if (!state.client || !state.connected || !state.sessionKey.trim()) {
    state.chatPalaceContinuity = null;
    state.chatPalaceContinuityError = null;
    return false;
  }
  if (!opts?.quiet) {
    state.chatPalaceContinuityLoading = true;
  }
  state.chatPalaceContinuityError = null;
  // JAISIU-session-isolation — gate every write by the session key still
  // matching, the same way `loadChatContextBreakdown` and
  // `loadSessionEnvironment` do. This fetcher was the one inspector panel
  // that did not: a request started for chat-A painted its snapshot onto
  // chat-B if the operator switched while the network was in flight, and a
  // transient failure blanked the panel for whichever session was on screen
  // rather than the one that had actually errored.
  const requestedKey = state.sessionKey.trim();
  try {
    const res = await state.client.request<PalaceContinuitySnapshot>("sessions.palaceContinuity", {
      key: state.sessionKey,
      ...(opts?.includeMemorySnippets ? { includeMemorySnippets: true } : {}),
    });
    if (state.sessionKey.trim() === requestedKey) {
      state.chatPalaceContinuity = res ?? null;
    }
    return true;
  } catch (err) {
    if (state.sessionKey.trim() === requestedKey) {
      state.chatPalaceContinuityError = formatPalaceContinuityError(err);
      // Keep the previous snapshot rather than nulling it: a transient
      // failure should not blank a panel that was showing real continuity.
      state.chatPalaceContinuity = state.chatPalaceContinuity ?? null;
    }
    return true;
  } finally {
    if (state.sessionKey.trim() === requestedKey) {
      state.chatPalaceContinuityLoading = false;
    }
  }
}

export function openChatPalaceContinuity(state: ChatPalaceContinuityState) {
  state.chatPalaceContinuityOpen = true;
  if (!snapshotMatchesSession(state.chatPalaceContinuity, state.sessionKey)) {
    void loadChatPalaceContinuity(state, { includeMemorySnippets: true });
    return;
  }
  void loadChatPalaceContinuity(state, { quiet: true, includeMemorySnippets: true });
}

export function closeChatPalaceContinuity(state: ChatPalaceContinuityState) {
  state.chatPalaceContinuityOpen = false;
}

export function toggleChatPalaceContinuity(state: ChatPalaceContinuityState) {
  if (state.chatPalaceContinuityOpen) {
    closeChatPalaceContinuity(state);
    return;
  }
  openChatPalaceContinuity(state);
}

export async function clearChatPalaceContinuityForSession(state: ChatPalaceContinuityState) {
  if (
    !state.client ||
    !state.connected ||
    !state.sessionKey.trim() ||
    state.chatPalaceContinuityClearing
  ) {
    return;
  }
  state.chatPalaceContinuityClearing = true;
  state.chatPalaceContinuityError = null;
  try {
    await state.client.request("sessions.palaceContinuity.clear", { key: state.sessionKey });
    await loadChatPalaceContinuity(state, { includeMemorySnippets: true });
  } catch (err) {
    state.chatPalaceContinuityError = formatPalaceContinuityError(err);
  } finally {
    state.chatPalaceContinuityClearing = false;
  }
}

export function setChatPalaceContinuityDraft(state: ChatPalaceContinuityState, draft: string) {
  state.chatPalaceContinuityDraft = draft;
}

export function setChatPalaceContinuityDraftCategory(
  state: ChatPalaceContinuityState,
  category: "fact" | "decision",
) {
  state.chatPalaceContinuityDraftCategory = category;
}

export async function removeChatPalaceContinuityPoint(
  state: ChatPalaceContinuityState,
  pointId: string,
) {
  const id = pointId.trim();
  if (
    !state.client ||
    !state.connected ||
    !state.sessionKey.trim() ||
    !id ||
    state.chatPalaceContinuityMutating
  ) {
    return;
  }
  state.chatPalaceContinuityMutating = true;
  state.chatPalaceContinuityError = null;
  try {
    await state.client.request("sessions.palaceContinuity.remove", {
      key: state.sessionKey,
      pointId: id,
    });
    await loadChatPalaceContinuity(state, { includeMemorySnippets: true });
  } catch (err) {
    state.chatPalaceContinuityError = formatPalaceContinuityError(err);
  } finally {
    state.chatPalaceContinuityMutating = false;
  }
}

// generateIdempotencyKey() was removed: the gateway's
// `sessions.palaceContinuity.add` schema is strict
// (`additionalProperties: false`) and rejects wire-level idempotency
// keys, so generating and sending one caused every "Add" click to fail
// with `GatewayRequestError: invalid sessions.palaceContinuity.add
// params: at root: unexpected property 'idempotencyKey'`. Duplicate-add
// protection is now handled entirely client-side by the
// `chatPalaceContinuityMutating` single-flight guard in
// `addChatPalaceContinuityPoint`. Server-side dedupe (with a real
// schema-accepted idempotency key) is a separate ticket.

export async function addChatPalaceContinuityPoint(state: ChatPalaceContinuityState) {
  const text = state.chatPalaceContinuityDraft.trim();
  if (
    !state.client ||
    !state.connected ||
    !state.sessionKey.trim() ||
    !text ||
    state.chatPalaceContinuityMutating
  ) {
    return;
  }
  // Mark the request in-flight before any awaits so concurrent clicks see
  // `chatPalaceContinuityMutating` and bail out (single-flight per tab).
  // We use a local marker rather than a wire-level idempotencyKey because
  // the `sessions.palaceContinuity.add` schema is strict — it rejects
  // unknown properties. Server-side dedupe is a separate ticket.
  if (!state.chatPalaceContinuityPending) {
    state.chatPalaceContinuityPending = new Set<string>();
  }
  const pendingMarker = `palace-continuity-add:${Date.now()}:${Math.random().toString(36).slice(2)}`;
  state.chatPalaceContinuityPending.add(pendingMarker);
  state.chatPalaceContinuityMutating = true;
  state.chatPalaceContinuityError = null;
  try {
    await state.client.request("sessions.palaceContinuity.add", {
      key: state.sessionKey,
      text,
      category: state.chatPalaceContinuityDraftCategory,
    });
    state.chatPalaceContinuityDraft = "";
    await loadChatPalaceContinuity(state, { includeMemorySnippets: true });
    triggerPalaceContinuityFlash(state);
  } catch (err) {
    state.chatPalaceContinuityError = formatPalaceContinuityError(err);
    // The marker is cleared in `finally` regardless of outcome. On a
    // transport error (network blip / WS drop), the write may or may
    // not have landed — the next snapshot fetch in
    // `loadChatPalaceContinuity` will reflect what the gateway actually
    // persisted. Duplicate-add protection comes from the
    // `chatPalaceContinuityMutating` guard above, not from server-side
    // dedupe.
  } finally {
    state.chatPalaceContinuityMutating = false;
    state.chatPalaceContinuityPending.delete(pendingMarker);
  }
}

export function formatContinuityTimestamp(iso: string): string {
  const parsed = Date.parse(iso);
  if (Number.isNaN(parsed)) {
    return iso;
  }
  return new Date(parsed).toLocaleString(undefined, {
    month: "short",
    day: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
}

export function hasStructuredContinuity(payload: SessionContinuityPayload | undefined): boolean {
  if (!payload) {
    return false;
  }
  return Boolean(
    payload.activeTask ||
    payload.pendingSubTasks.length > 0 ||
    payload.recentDecisions.length > 0 ||
    payload.constraints.length > 0 ||
    payload.lastWorkerFindings,
  );
}
